import { useState, useRef, useCallback } from 'react';
import { MAX_VOICE_MEMO_DURATION_SECONDS, VOICE_MEMO_WAVEFORM_SAMPLES } from '@goodchat/shared/constants';

interface RecordingResult {
  blob: Blob;
  durationSeconds: number;
  waveformData: number[];
}

export function useVoiceRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const waveformSamplesRef = useRef<number[]>([]);
  const startTimeRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const rafRef = useRef<number | null>(null);
  const resolveStopRef = useRef<((result: RecordingResult) => void) | null>(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    waveformSamplesRef.current = [];
    resolveStopRef.current = null;
  }, []);

  const collectWaveform = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;

    const data = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(data);

    // RMS of the time-domain data, normalized to 0-1
    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      const v = (data[i] - 128) / 128;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / data.length);
    waveformSamplesRef.current.push(Math.min(1, rms * 3)); // amplify slightly

    rafRef.current = requestAnimationFrame(collectWaveform);
  }, []);

  const startRecording = useCallback(async () => {
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      console.error('Failed to start recording:', err);
      return;
    }
    streamRef.current = stream;

    // Set up Web Audio API for waveform
    const audioContext = new AudioContext();
    audioContextRef.current = audioContext;
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    analyserRef.current = analyser;

    // Choose mime type
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : '';

    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    mediaRecorderRef.current = recorder;
    chunksRef.current = [];
    waveformSamplesRef.current = [];

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
      const durationSeconds = (Date.now() - startTimeRef.current) / 1000;

      // Downsample waveform to target number of samples
      const raw = waveformSamplesRef.current;
      const waveformData = downsampleWaveform(raw, VOICE_MEMO_WAVEFORM_SAMPLES);

      if (resolveStopRef.current) {
        resolveStopRef.current({ blob, durationSeconds, waveformData });
      }
      cleanup();
    };

    startTimeRef.current = Date.now();
    recorder.start(200); // collect data every 200ms
    setIsRecording(true);
    setDuration(0);

    // Duration timer
    timerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      setDuration(elapsed);

      // Auto-stop at max duration
      if (elapsed >= MAX_VOICE_MEMO_DURATION_SECONDS) {
        if (mediaRecorderRef.current?.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      }
    }, 100);

    // Start waveform collection
    collectWaveform();
  }, [cleanup, collectWaveform]);

  const stopRecording = useCallback((): Promise<RecordingResult> => {
    return new Promise((resolve) => {
      resolveStopRef.current = resolve;
      setIsRecording(false);
      setDuration(0);
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    });
  }, []);

  const cancelRecording = useCallback(() => {
    setIsRecording(false);
    setDuration(0);
    if (mediaRecorderRef.current?.state === 'recording') {
      // Remove onstop handler to prevent resolving
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }
    cleanup();
  }, [cleanup]);

  return { isRecording, duration, startRecording, stopRecording, cancelRecording };
}

function downsampleWaveform(raw: number[], targetSamples: number): number[] {
  if (raw.length <= targetSamples) {
    // Pad with zeros if not enough samples
    const result = [...raw];
    while (result.length < targetSamples) result.push(0);
    return result;
  }

  const result: number[] = [];
  const step = raw.length / targetSamples;
  for (let i = 0; i < targetSamples; i++) {
    const start = Math.floor(i * step);
    const end = Math.floor((i + 1) * step);
    let max = 0;
    for (let j = start; j < end; j++) {
      if (raw[j] > max) max = raw[j];
    }
    result.push(max);
  }
  return result;
}
